/* eslint-disable react-hooks/exhaustive-deps */
import { useAppDispatch, useAppSelector } from "app/hooks";
import { isJustSuperAdmin } from "utils";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import {
  fetchPermissions,
  fetchRoles,
  inviteAdmin,
} from "features/main/admin/thunkActions";
import { useDisclosure } from "@chakra-ui/react";
import { AccessRole } from "components/Modal/Admin/AccessPermissionsModal";
import useOtpConfirmation from "utils/useOtpConfirmation";

const selectOptions: ObjectKeyValuePair[] = [
  {
    label: "Basic",
    value: "basic",
  },
  {
    label: "Advanced",
    value: "advanced",
  },
];

const UseInviteNewAdminModel = () => {
  const dispatch = useAppDispatch();
  const { accountDetails } = useAppSelector((state) => state.profile);
  const [step, setStep] = useState<number>(1);

  const {
    isOpen: isOpenPermissions,
    onOpen: onOpenPermissions,
    onClose: onClosePermissions,
  } = useDisclosure()

  const [userEmail, setUserEmail] = useState<string>("");

  const [showInviteAdminModal, setShowInviteAdminModal] = useState<boolean>(false);

  const [role, setRole] = useState<AccessRole | "">("")
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const initialFormValues: IInviteAdmin = {
    firstName: "",
    lastName: "",
    email: "",
    designation: "",
  };

  const inviteAdminSchema = Yup.object().shape({
    email: Yup.string()
      .email("Please enter a valid email")
      .min(5, "Must be longer than 5 characters")
      .required("Email is required"),
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    designation: Yup.string().required("Designation is required"),
  });

  const handleSelectRole = (nextRole: AccessRole) => {
    if (nextRole !== role) setSelectedIds([]);
    setRole(nextRole);
    onOpenPermissions();
  };

  const handleGetRole = (value: string) => {
    const newOptions = [...selectOptions];

    const x = newOptions.find((a) => a.value === value);

    if (x) {
      return { label: x.label, value: x.value } as ObjectKeyValuePair;
    }

    return { label: "Select a role", value: "" } as ObjectKeyValuePair;
  };

  const sendInvitation = (payload: IInviteAdmin & { otp_code: string }) => {
    setUserEmail(payload.email);
    const onSuccess = (data: string) => {
      if (data) {
        setStep(2);
      }
    };

    const onFailure = (errorResponse: IErrorResponse) => {
      toast.error(errorResponse.message);
    };

    return dispatch(inviteAdmin({ payload, onSuccess, onFailure }))
      .then((result) => inviteAdmin.fulfilled.match(result));
  };

  const { requestOtp, otpConfirmation } = useOtpConfirmation<IInviteAdmin>(sendInvitation);

  const handleInviteAdmin = (
    payload: IInviteAdmin,
    setSubmitting: (data: boolean) => void,
  ) => {
    const newPayload = {
      ...payload,
      name: `${payload.firstName} ${payload.lastName}`,
      role,
      permissions: [...selectedIds],
    };
    delete newPayload.firstName;
    delete newPayload.lastName;
    requestOtp(newPayload, setSubmitting);
  };

  const handleFetchPermissions = () => {
    const onFailure = (errorResponse: IErrorResponse) => {
      toast.error(errorResponse.message);
    };

    dispatch(fetchPermissions({ onFailure }));
  };

  const handleFetchRoles = () => {
    const onFailure = (errorResponse: IErrorResponse) => {
      toast.error(errorResponse.message);
    };

    dispatch(fetchRoles({ onFailure }));
  };

  useEffect(() => {
    if (!isJustSuperAdmin(accountDetails?.admin?.role)) return;

    handleFetchPermissions();
    handleFetchRoles();
  }, [dispatch, accountDetails]);

  return {
    step,
    userEmail,
    role,
    setRole,
    selectOptions,
    inviteAdminSchema,
    initialFormValues,
    handleInviteAdmin,
    otpConfirmation,
    handleGetRole,
    selectedIds,
    setSelectedIds,
    isOpenPermissions,
    onClosePermissions,
    handleSelectRole,
    showInviteAdminModal,
    setShowInviteAdminModal
  };
};

export default UseInviteNewAdminModel;
